import React from 'react';

import {
  NexoyaFunnelStepType,
  NexoyaFunnelStepV2,
  NexoyaOptimizedDailyBudget,
  NexoyaOptimizedFunnelStep,
  NexoyaOptimizedTarget,
  NexoyaOptimizedTotal,
  NexoyaPortfolioType,
} from '../../../../../types';

import FormattedCurrency from '../../../../../components/FormattedCurrency';
import NumberValue from '../../../../../components/NumberValue';
import { PortfolioTargetTypeSwitch, PortfolioTypeSwitch } from '../../../../../components/PortfolioTypeSwitch';
import { sortTypes } from '../../../../../components/Table/sortTypes';
import Tooltip from '../../../../../components/Tooltip';
import Typography from '../../../../../components/Typography';
import SvgWarningTwo from '../../../../../components/icons/WarningTwo';
import { HoverableTooltip } from '../../../../../components-ui/HoverCard';
import { Info, Pencil } from 'lucide-react';

import { BigHeaderCell, TotalHeaderCell } from '../../../styles/OptimizationProposal';

import { withSortSkipped } from '../withSortSkipped';
import { formatWeekdayWithDates, getOptiAttributedTooltipContent, isNullOrUndefined } from '../utils';
import { ActionsCornerElementHeader, TotalHeaderCellElement } from './cell-renderers';
import { ACTIONS_HEADER_ID, CHANNEL_HEADER_ID, IMPACT_GROUP_HEADER_ID, LABEL_HEADER_ID } from './constants';

const getGroupedColumns = ({
  headerText,
  accessor,
  optimizedTotal,
  tableMetricsSwitch,
  tableViewSwitch,
  portfolioType,
  isBaselinePredictionRescaled,
  ignoreWeekdays,
  optimizationStartDate,
  onEditProposedSpend,
  onEditProposedTarget,
}: {
  headerText: string;
  accessor: string;
  optimizedTotal: NexoyaOptimizedTotal;
  tableMetricsSwitch: string;
  tableViewSwitch: string;
  portfolioType: NexoyaPortfolioType;
  isBaselinePredictionRescaled: boolean;
  ignoreWeekdays?: string[];
  optimizationStartDate?: string;
  onEditProposedSpend?: (() => void) | null;
  onEditProposedTarget?: (() => void) | null;
}) =>
  [
    {
      Header: ActionsCornerElementHeader,
      accessor: ACTIONS_HEADER_ID,
      disableSortBy: true,
      disableHiding: true,
      tableManagerHeader: <BigHeaderCell>{headerText}</BigHeaderCell>,
      columns: [
        {
          Header: TotalHeaderCellElement,
          accessor: 'total' + tableViewSwitch,
          isHiddenInManager: true,
          disableSortBy: true,
          disableHiding: true,
          columns: [
            {
              Header: headerText,
              accessor,
              id: accessor + tableViewSwitch,
              // `id` doubles tableViewSwitch, so react-table's columnId never matches the row-data
              // key — the columnId-based `jsxKey` can't find the cell. Close over `accessor` instead.
              sortType: withSortSkipped(sortTypes.createSortByJsxKeyNatural(accessor)),
              enableColumnResize: true,
              disableHiding: true,
            },
          ],
        },
      ],
    },
    portfolioType !== NexoyaPortfolioType.Budget &&
      getTargetColumns(optimizedTotal.target, isBaselinePredictionRescaled, onEditProposedTarget),
    ...getBudgetColumns(
      optimizedTotal.budget,
      false,
      ignoreWeekdays,
      optimizationStartDate,
      tableViewSwitch,
      onEditProposedSpend,
    ),
    ...(tableViewSwitch === 'all-content' ? getBiddingStrategyColumns() : []),
    ...getFunnelStepColumns({
      optimizedFunnelSteps: optimizedTotal.funnelSteps?.filter(Boolean),
      tableMetricsSwitch,
      isBaselinePredictionRescaled,
    }),
  ].filter(Boolean);

export const getChannelColumns = (
  optimizedTotal: NexoyaOptimizedTotal,
  tableMetricsSwitch: string,
  tableViewSwitch: string,
  portfolioType: NexoyaPortfolioType,
  isBaselinePredictionRescaled: boolean,
  ignoreWeekdays?: string[],
  optimizationStartDate?: string,
  onEditProposedSpend?: (() => void) | null,
  onEditProposedTarget?: (() => void) | null,
) =>
  getGroupedColumns({
    headerText: 'Channel',
    accessor: 'channel',
    optimizedTotal,
    tableMetricsSwitch,
    tableViewSwitch,
    portfolioType,
    isBaselinePredictionRescaled,
    ignoreWeekdays,
    optimizationStartDate,
    onEditProposedSpend,
    onEditProposedTarget,
  });

export const getLabelColumns = (
  optimizedTotal: NexoyaOptimizedTotal,
  tableMetricsSwitch: string,
  tableViewSwitch: string,
  portfolioType: NexoyaPortfolioType,
  isBaselinePredictionRescaled: boolean,
  ignoreWeekdays?: string[],
  optimizationStartDate?: string,
  onEditProposedSpend?: (() => void) | null,
  onEditProposedTarget?: (() => void) | null,
) =>
  getGroupedColumns({
    headerText: 'Label',
    accessor: 'label' + tableViewSwitch,
    optimizedTotal,
    tableMetricsSwitch,
    tableViewSwitch,
    portfolioType,
    isBaselinePredictionRescaled,
    ignoreWeekdays,
    optimizationStartDate,
    onEditProposedSpend,
    onEditProposedTarget,
  });

/**
 * The Optimize "By Label" view's columns.
 *
 * `contentLabelCategoryName` is APPENDED, deliberately last: the five other
 * grouped builders share one positional argument order, and inserting a
 * parameter into the middle of it would silently shift every caller's arguments
 * by one with a perfectly green build.
 *
 * The header is the chosen category's own name (fig3 reads "Country"), so the
 * sub-header row says what the column groups by. `'Label'` is the fallback for a
 * category with no name — never an empty header cell.
 */
export const getContentLabelColumns = (
  optimizedTotal: NexoyaOptimizedTotal,
  tableMetricsSwitch: string,
  tableViewSwitch: string,
  portfolioType: NexoyaPortfolioType,
  isBaselinePredictionRescaled: boolean,
  ignoreWeekdays?: string[],
  optimizationStartDate?: string,
  onEditProposedSpend?: (() => void) | null,
  onEditProposedTarget?: (() => void) | null,
  contentLabelCategoryName?: string,
) =>
  getGroupedColumns({
    headerText: contentLabelCategoryName || 'Label',
    accessor: 'contentLabel' + tableViewSwitch,
    optimizedTotal,
    tableMetricsSwitch,
    tableViewSwitch,
    portfolioType,
    isBaselinePredictionRescaled,
    ignoreWeekdays,
    optimizationStartDate,
    onEditProposedSpend,
    onEditProposedTarget,
  });

// 'attribution-groups' is the UI-facing name; maps to 'attributionRule' in the data model
export const getAttributionGroupColumns = (
  optimizedTotal: NexoyaOptimizedTotal,
  tableMetricsSwitch: string,
  tableViewSwitch: string,
  portfolioType: NexoyaPortfolioType,
  isBaselinePredictionRescaled: boolean,
  ignoreWeekdays?: string[],
  optimizationStartDate?: string,
  onEditProposedSpend?: (() => void) | null,
  onEditProposedTarget?: (() => void) | null,
) =>
  getGroupedColumns({
    headerText: 'Attribution Group',
    accessor: 'attributionRule' + tableViewSwitch,
    optimizedTotal,
    tableMetricsSwitch,
    tableViewSwitch,
    portfolioType,
    isBaselinePredictionRescaled,
    ignoreWeekdays,
    optimizationStartDate,
    onEditProposedSpend,
    onEditProposedTarget,
  });

export const getAllContentsColumns = (
  optimizedTotal: NexoyaOptimizedTotal,
  tableMetricsSwitch: string,
  tableViewSwitch: string,
  hasTargets: boolean,
  hasLifetimeBudget: boolean,
  hasLabels: boolean,
  isBaselinePredictionRescaled: boolean,
  isPortfolioAttributed: boolean,
  ignoreWeekdays?: string[],
  optimizationStartDate?: string,
  onEditProposedSpend?: (() => void) | null,
) =>
  [
    {
      Header: ActionsCornerElementHeader,
      accessor: ACTIONS_HEADER_ID,
      tableManagerHeader: <BigHeaderCell>Content</BigHeaderCell>,
      disableSortBy: true,
      disableHiding: true,
      columns: [
        {
          Header: '',
          accessor: 'editRowHeader',
          id: 'editRowHeader',
          width: '48',
          isHiddenInManager: true,
          disableSortBy: true,
          disableHiding: true,
          columns: [
            {
              Header: '',
              accessor: 'editRow',
              id: 'editRow',
              width: '48',
              isHiddenInManager: true,
              disableSortBy: true,
              disableHiding: true,
            },
          ],
        },
        {
          Header: TotalHeaderCellElement,
          accessor: 'total',
          disableSortBy: true,
          className: 'border-right',
          enableColumnResize: true,
          disableHiding: true,
          columns: [
            {
              Header: 'Content',
              accessor: 'content',
              className: 'border-right',
              sortType: withSortSkipped(sortTypes.jsxKey),
              enableColumnResize: true,
              disableHiding: true,
            },
          ],
        },
      ],
    },
    {
      Header: '',
      accessor: 'emptyStatusHeader',
      id: 'emptyStatusHeader',
      isHiddenInManager: false,
      tableManagerHeader: <BigHeaderCell>Status</BigHeaderCell>,
      disableSortBy: true,
      columns: [
        {
          id: 'statusHeader',
          Header: '',
          accessor: 'statusHeader',
          disableSortBy: true,
          columns: [
            {
              id: 'status',
              accessor: 'status',
              Header: 'Status',
              sortType: withSortSkipped(sortTypes.jsxKey),
            },
          ],
        },
      ],
    },
    {
      Header: '',
      accessor: 'emptyChannelHeader',
      id: 'emptyChannelHeader',
      isHiddenInManager: false,
      tableManagerHeader: <BigHeaderCell>Channel</BigHeaderCell>,
      disableSortBy: true,
      columns: [
        {
          id: CHANNEL_HEADER_ID,
          Header: '',
          accessor: CHANNEL_HEADER_ID,
          disableSortBy: true,
          columns: [
            {
              id: 'channel',
              accessor: 'channel',
              Header: 'Channel',
              sortType: withSortSkipped(sortTypes.jsxKey),
            },
          ],
        },
      ],
    },
    !isPortfolioAttributed
      ? {
          Header: '',
          accessor: 'emptyImpactGroupHeader',
          id: 'emptyImpactGroupHeader',
          isHiddenInManager: false,
          tableManagerHeader: <BigHeaderCell>Impact group</BigHeaderCell>,
          disableSortBy: true,
          columns: [
            {
              id: IMPACT_GROUP_HEADER_ID,
              Header: '',
              accessor: IMPACT_GROUP_HEADER_ID,
              disableSortBy: true,
              columns: [
                {
                  id: 'impactGroup',
                  accessor: 'impactGroup',
                  Header: 'Impact group',
                  sortType: withSortSkipped(sortTypes.jsxKey),
                },
              ],
            },
          ],
        }
      : null,
    hasLabels
      ? {
          Header: '',
          accessor: 'emptyLabelHeader',
          id: 'emptyLabelHeader',
          isHiddenInManager: false,
          tableManagerHeader: <BigHeaderCell>Label</BigHeaderCell>,
          disableSortBy: true,
          columns: [
            {
              id: LABEL_HEADER_ID,
              Header: '',
              accessor: LABEL_HEADER_ID,
              disableSortBy: true,
              columns: [
                {
                  id: 'label' + tableViewSwitch,
                  accessor: 'label' + tableViewSwitch,
                  Header: 'Label',
                  sortType: withSortSkipped(sortTypes.jsxKey),
                },
              ],
            },
          ],
        }
      : null,
    ...getBudgetColumns(
      optimizedTotal.budget,
      hasLifetimeBudget,
      ignoreWeekdays,
      optimizationStartDate,
      tableViewSwitch,
      onEditProposedSpend,
    ),
    ...(tableViewSwitch === 'all-content' && hasTargets ? getBiddingStrategyColumns() : []),
    ...getFunnelStepColumns({
      optimizedFunnelSteps: optimizedTotal.funnelSteps?.filter(Boolean),
      tableMetricsSwitch,
      isBaselinePredictionRescaled,
    }),
  ].filter(Boolean);

export const getImpactGroupColumns = (
  optimizedTotal: NexoyaOptimizedTotal,
  tableMetricsSwitch: string,
  tableViewSwitch: string,
  portfolioType: NexoyaPortfolioType,
  isBaselinePredictionRescaled: boolean,
  ignoreWeekdays?: string[],
  optimizationStartDate?: string,
  onEditProposedSpend?: (() => void) | null,
  onEditProposedTarget?: (() => void) | null,
) =>
  getGroupedColumns({
    headerText: 'Impact group',
    accessor: 'impactGroup',
    optimizedTotal,
    tableMetricsSwitch,
    tableViewSwitch,
    portfolioType,
    isBaselinePredictionRescaled,
    ignoreWeekdays,
    optimizationStartDate,
    onEditProposedSpend,
    onEditProposedTarget,
  });

const getBudgetColumns = (
  budgetTotals: NexoyaOptimizedDailyBudget,
  hasLifetimeBudget: boolean,
  ignoreWeekdays?: string[],
  optimizationStartDate?: string,
  tableViewSwitch?: string,
  onEditProposedSpend?: (() => void) | null,
) => [
  // --- SPEND group: Avg. daily spend | Proposed daily budget / Predicted daily spend | % Change ---
  {
    id: 'bHeaderPreviousBudget',
    title: 'Avg. daily spend',
    className: 'border-left',
    Header: (
      <BigHeaderCell>
        <Tooltip
          variant="dark"
          style={{ maxWidth: 420, wordBreak: 'break-word' }}
          popperProps={{
            style: {
              zIndex: 3305,
            },
          }}
          content={
            <Typography withEllipsis={false} style={{ fontSize: 12 }}>
              This is the average daily spend of the last 7 eligible days or since the last budget application.
              {ignoreWeekdays && ignoreWeekdays.length > 0 && (
                <>
                  <br />
                  <br />
                  <span className="font-semibold">Days ignored in the calculation:</span>
                  <br />
                  {ignoreWeekdays.map((weekday) => (
                    <>
                      {formatWeekdayWithDates(weekday, optimizationStartDate)}
                      <br />
                    </>
                  ))}
                </>
              )}
            </Typography>
          }
        >
          <HoverableTooltip>
            <div>Avg. daily spend</div>
          </HoverableTooltip>
        </Tooltip>
      </BigHeaderCell>
    ),
    disableSortBy: true,
    columns: [
      {
        id: 'totalPreviousBudget',
        accessor: 'totalPreviousBudget',
        className: 'border-left',
        Header: (
          <TotalHeaderCell>
            <FormattedCurrency amount={budgetTotals.spent} />
          </TotalHeaderCell>
        ),
        disableSortBy: true,
        columns: [
          {
            id: 'previousDailyBudget',
            accessor: 'previousDailyBudget',
            className: 'border-left',
            sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
            Header: 'Value',
          },
        ],
      },
    ],
  },
  {
    id: 'bHeaderProposedBudget',
    title: 'Predicted daily spend',
    Header: (
      <BigHeaderCell>
        <Tooltip
          variant="dark"
          style={{ maxWidth: 348, wordBreak: 'break-word' }}
          popperProps={{
            style: {
              zIndex: 3305,
            },
          }}
          content={
            <Typography withEllipsis={false} style={{ fontSize: 12 }}>
              This is the predicted daily spend for the optimization timeframe.
            </Typography>
          }
        >
          <HoverableTooltip>
            <div>Predicted daily spend</div>
          </HoverableTooltip>
        </Tooltip>
      </BigHeaderCell>
    ),
    disableSortBy: true,
    columns: [
      {
        id: 'totalProposedBudget',
        accessor: 'totalProposedBudget',
        disableSortBy: true,
        Header: (
          <TotalHeaderCell>
            <FormattedCurrency amount={budgetTotals.proposed} />
            {onEditProposedSpend ? (
              <Tooltip
                variant="dark"
                style={{ maxWidth: 320, wordBreak: 'break-word' }}
                popperProps={{ style: { zIndex: 3305 } }}
                content={
                  <Typography withEllipsis={false} style={{ fontSize: 12 }}>
                    Edit the predicted daily spend to request an updated optimization proposal.
                  </Typography>
                }
              >
                <button
                  type="button"
                  aria-label="Edit predicted daily spend"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditProposedSpend();
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  <Pencil size={14} className="text-neutral-400" />
                </button>
              </Tooltip>
            ) : null}
          </TotalHeaderCell>
        ),
        columns: [
          {
            id: 'proposedDailyBudget',
            accessor: 'proposedDailyBudget',
            sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
            minWidth: 200,
            Header: 'Value',
          },
        ],
      },
    ].filter(Boolean),
  },
  {
    id: 'bHeaderChangePercent',
    title: '% Change',
    className: hasLifetimeBudget && tableViewSwitch !== 'all-content' ? '' : 'border-right',
    Header: (
      <BigHeaderCell>
        <Tooltip
          variant="dark"
          style={{ maxWidth: 370, wordBreak: 'break-word' }}
          popperProps={{
            style: {
              zIndex: 3305,
            },
          }}
          content={
            <Typography withEllipsis={false} style={{ fontSize: 12 }}>
              This is the difference between the average daily spend and the predicted daily spend.
            </Typography>
          }
        >
          <HoverableTooltip>
            <div>% Change</div>
          </HoverableTooltip>
        </Tooltip>
      </BigHeaderCell>
    ),
    disableSortBy: true,
    columns: [
      {
        id: 'totalBudgetChange',
        className: hasLifetimeBudget && tableViewSwitch !== 'all-content' ? '' : 'border-right',
        Header: (
          <TotalHeaderCell>
            <NumberValue
              style={{ color: '#a0a2ad' }}
              value={budgetTotals.changePercent}
              variant={budgetTotals.changePercent > 0 ? 'positive' : 'negative'}
              showChangePrefix
              datatype={{
                suffix: true,
                symbol: '%',
              }}
            />
          </TotalHeaderCell>
        ),
        disableSortBy: true,
        columns: [
          {
            id: 'proposedDailyBudgetChange',
            accessor: 'proposedDailyBudgetChange',
            className: hasLifetimeBudget && tableViewSwitch !== 'all-content' ? '' : 'border-right',
            sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
            Header: '% Change',
          },
        ],
      },
    ].filter(Boolean),
  },
  // --- BUDGET group (all-content view only): Current daily budget | Proposed daily budget (new) | % Change ---
  ...(tableViewSwitch === 'all-content'
    ? [
        {
          id: 'bHeaderCurrentBudget',
          title: 'Current daily budget',
          Header: (
            <BigHeaderCell>
              <div>Current daily budget</div>
            </BigHeaderCell>
          ),
          disableSortBy: true,
          columns: [
            {
              id: 'totalCurrentBudget',
              accessor: 'totalCurrentBudget',
              disableSortBy: true,
              Header: '',
              columns: [
                {
                  id: 'platformBudget',
                  accessor: 'platformBudget',
                  className: 'budget',
                  minWidth: 200,
                  sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
                  Header: 'Value',
                },
              ],
            },
          ],
        },
        {
          id: 'bHeaderNewProposedBudget',
          title: 'Proposed daily budget',
          Header: (
            <BigHeaderCell>
              <Tooltip
                variant="dark"
                style={{ maxWidth: 520, wordBreak: 'break-word' }}
                popperProps={{ style: { zIndex: 3305 } }}
                content={
                  <div style={{ fontSize: 12, lineHeight: 1.6, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <span>This is the proposed daily budget for the optimization timeframe.</span>
                    <span>
                      <strong>The difference between Predicted Daily Spend and Proposed Daily Budget</strong> reflects
                      how much the campaign is expected to spend based on historical performance and delivery patterns.
                    </span>
                    <span>
                      This value may be higher or lower because each marketing campaign has its own delivery capacity,
                      auction dynamics, and scaling potential.
                    </span>
                    <span>
                      The proposed budget therefore accounts for the campaign's ability to scale and spend effectively.
                    </span>
                  </div>
                }
              >
                <HoverableTooltip>
                  <div className="flex items-center gap-1 pb-0.5">
                    Proposed daily budget
                    <Info className="size-4 text-neutral-400" />
                  </div>
                </HoverableTooltip>
              </Tooltip>
            </BigHeaderCell>
          ),
          disableSortBy: true,
          columns: [
            {
              id: 'totalNewProposedBudget',
              accessor: 'totalNewProposedBudget',
              disableSortBy: true,
              Header: '',
              columns: [
                {
                  id: 'proposedBudgetFromTeamCurrency',
                  accessor: 'proposedBudgetFromTeamCurrency',
                  sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
                  minWidth: 230,
                  Header: 'Value',
                },
              ],
            },
          ],
        },
        {
          id: 'bHeaderNewChangePercent',
          title: '% Change',
          className: 'border-right',
          Header: (
            <BigHeaderCell>
              <div>% Change</div>
            </BigHeaderCell>
          ),
          disableSortBy: true,
          columns: [
            {
              id: 'totalNewBudgetChange',
              className: 'border-right',
              disableSortBy: true,
              Header: '',
              columns: [
                {
                  id: 'proposedBudgetChangePercent',
                  accessor: 'proposedBudgetChangePercent',
                  className: 'budget border-right',
                  sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
                  Header: '% Change',
                },
              ],
            },
          ],
        },
      ]
    : []),
  {
    id: 'bHeaderLifetimeBudget',
    title: 'Lifetime budget',
    Header: <BigHeaderCell>Lifetime Budget</BigHeaderCell>,
    disableSortBy: true,
    className: 'border-right',
    columns: [
      hasLifetimeBudget && {
        id: 'totalLifetimeBudget',
        className: 'border-right',
        Header: '',
        disableSortBy: true,
        columns: [
          {
            className: 'border-right',
            id: 'lifetimeBudgetSegments',
            accessor: 'lifetimeBudgetSegments',
            sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
            Header: 'Value',
          },
        ],
      },
    ].filter(Boolean),
  },
];

const getBiddingStrategyColumns = () => [
  {
    id: 'targetHeaderInitial',
    Header: <BigHeaderCell>Current target</BigHeaderCell>,
    disableSortBy: true,
    title: 'Value',
    columns: [
      {
        id: 'totalBiddingStrategy',
        accessor: 'totalBiddingStrategy',
        Header: '',
        disableSortBy: true,
        columns: [
          {
            id: 'initialBiddingStrategy',
            accessor: 'initialBiddingStrategy',
            Header: 'Value',
            sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
          },
        ],
      },
    ],
  },
  {
    id: 'targetHeaderProposed',
    Header: <BigHeaderCell>Proposed target</BigHeaderCell>,
    disableSortBy: true,
    title: 'Value',
    columns: [
      {
        id: 'totalProposedBiddingStrategy',
        accessor: 'totalProposedBiddingStrategy',
        Header: '',
        disableSortBy: true,
        columns: [
          {
            id: 'proposedBiddingStrategy',
            accessor: 'proposedBiddingStrategy',
            Header: 'Value',
            sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
          },
        ],
      },
    ],
  },
  {
    id: 'targetHeaderChangePercent',
    Header: <BigHeaderCell>% Change</BigHeaderCell>,
    disableSortBy: true,
    title: 'Target',
    className: 'border-right',
    columns: [
      {
        id: 'totalBiddingStrategyChangePercent',
        className: 'border-right',
        Header: '',
        disableSortBy: true,
        columns: [
          {
            id: 'biddingStrategyChangePercent',
            accessor: 'biddingStrategyChangePercent',
            Header: '% Change',
            className: 'border-right',
            sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
          },
        ],
      },
    ],
  },
];

const getTargetColumns = (
  targetTotals: NexoyaOptimizedTarget,
  isBaselinePredictionRescaled: boolean,
  onEditProposedTarget?: (() => void) | null,
) => ({
  id: 'tHeader',
  title: 'Target',
  Header: <BigHeaderCell>Portfolio Target</BigHeaderCell>,
  disableSortBy: true,
  columns: [
    {
      id: 'totalPreviousTarget',
      accessor: 'totalPreviousTarget',
      className: 'border-left',
      Header: (
        <TotalHeaderCell>
          <PortfolioTargetTypeSwitch
            renderForCPAType={() => <FormattedCurrency amount={targetTotals?.previous} />}
            renderForROASType={() => (
              <NumberValue
                value={targetTotals?.previous}
                datatype={{
                  suffix: true,
                  symbol: '%',
                }}
              />
            )}
          />
        </TotalHeaderCell>
      ),
      disableSortBy: true,
      columns: [
        {
          id: 'previousDailyTarget',
          accessor: 'previousDailyTarget',
          className: 'border-left',
          sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
          minWidth: 155,
          Header: (
            <Tooltip
              variant="dark"
              style={{ maxWidth: 420, wordBreak: 'break-word' }}
              popperProps={{
                style: {
                  zIndex: 3305,
                },
              }}
              content={
                <Typography withEllipsis={false} style={{ fontSize: 12 }}>
                  This is the predicted portfolio target without optimization.
                </Typography>
              }
            >
              <HoverableTooltip>
                <div>Predicted baseline</div>
              </HoverableTooltip>
            </Tooltip>
          ),
        },
      ],
    },
    {
      id: 'totalProposedTarget',
      accessor: 'totalProposedTarget',
      disableSortBy: true,
      className: 'target',
      Header: (
        <TotalHeaderCell>
          <PortfolioTargetTypeSwitch
            renderForCPAType={() => <FormattedCurrency amount={targetTotals?.proposed} />}
            renderForROASType={() => (
              <NumberValue
                value={targetTotals?.proposed}
                datatype={{
                  suffix: true,
                  symbol: '%',
                }}
              />
            )}
          />
          {onEditProposedTarget ? (
            <Tooltip
              variant="dark"
              style={{ maxWidth: 320, wordBreak: 'break-word' }}
              popperProps={{ style: { zIndex: 3305 } }}
              content={
                <Typography withEllipsis={false} style={{ fontSize: 12 }}>
                  Edit the portfolio target to request an updated optimization proposal.
                </Typography>
              }
            >
              <button
                type="button"
                aria-label="Edit portfolio target"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditProposedTarget();
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  border: 0,
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <Pencil size={14} className="text-neutral-400" />
              </button>
            </Tooltip>
          ) : null}
        </TotalHeaderCell>
      ),
      columns: [
        {
          id: 'proposedDailyTarget',
          accessor: 'proposedDailyTarget',
          className: 'target',
          sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
          Header: (
            <Tooltip
              variant="dark"
              style={{ maxWidth: 335, wordBreak: 'break-word' }}
              popperProps={{
                style: {
                  zIndex: 3305,
                },
              }}
              content={
                <Typography withEllipsis={false} style={{ fontSize: 12 }}>
                  This is the suggested portfolio target at the end of the timeframe of this optimization proposal.
                </Typography>
              }
            >
              <HoverableTooltip>
                <div>Proposed</div>
              </HoverableTooltip>
            </Tooltip>
          ),
        },
      ],
    },
    {
      id: 'totalTargetChange',
      Header: (
        <TotalHeaderCell>
          <NumberValue
            textWithColor
            showChangePrefix
            value={targetTotals?.changePercent}
            lowerIsBetter={targetTotals?.lowerIsBetter}
            variant={targetTotals?.changePercent > 0 ? 'positive' : 'negative'}
            datatype={{
              suffix: true,
              symbol: '%',
            }}
          />
        </TotalHeaderCell>
      ),
      disableSortBy: true,
      columns: [
        {
          id: 'proposedDailyTargetChange',
          accessor: 'proposedDailyTargetChange',
          sortType: withSortSkipped(sortTypes.jsxKeyAsNumber),
          Header: (
            <Tooltip
              variant="dark"
              style={{ maxWidth: 360, wordBreak: 'break-word' }}
              popperProps={{
                style: {
                  zIndex: 3305,
                },
              }}
              content={
                <Typography withEllipsis={false} style={{ fontSize: 12 }}>
                  <PortfolioTypeSwitch
                    renderForBudgetType={() =>
                      isBaselinePredictionRescaled
                        ? 'This is the uplift in % of the suggested portfolio target compared to the predicted baseline.'
                        : 'This is the difference between the predicted values with and without the suggested budget change.'
                    }
                    renderForTargetType={() =>
                      'This is the change in % of the suggested portfolio target compared to the predicted baseline.'
                    }
                  />
                </Typography>
              }
            >
              <HoverableTooltip>
                <PortfolioTypeSwitch
                  renderForBudgetType={() => <div>{isBaselinePredictionRescaled ? '% Uplift' : '% Change'}</div>}
                  renderForTargetType={() => <div>% Change</div>}
                />
              </HoverableTooltip>
            </Tooltip>
          ),
        },
      ],
    },
  ],
});

export const getFunnelStepColumns = ({
  optimizedFunnelSteps,
  tableMetricsSwitch,
  isBaselinePredictionRescaled,
}: {
  optimizedFunnelSteps: NexoyaOptimizedFunnelStep[];
  tableMetricsSwitch: string;
  isBaselinePredictionRescaled: boolean;
}) => {
  if (tableMetricsSwitch === 'roas') {
    return optimizedFunnelSteps
      .map((optimizedFunnelStep) => {
        const funnelStep = optimizedFunnelStep?.funnelStep;
        if (funnelStep?.type !== NexoyaFunnelStepType.ConversionValue) {
          return null;
        }

        const isAttributed = funnelStep?.isAttributed;

        const columns = createColumnStructure({
          idPrefix: 'funnelStepRoas',
          funnelStepId: funnelStep.funnelStepId,
          accessorPrefix: 'funnelStepRoas',
          isCostPerView: false,
          isPercent: true,
          funnelStepType: funnelStep?.type,
          isBaselinePredictionRescaled,
          funnelStepTitle: funnelStep?.title,
          isAttributed,
          total: {
            predicted: optimizedFunnelStep?.roas?.predicted,
            changePercent: optimizedFunnelStep?.roas?.changePercent,
          },
        });

        return {
          id: `funnel-step-header-${funnelStep?.funnelStepId}`,
          title: funnelStep?.title,
          disableSticky: true,
          Header: <BigHeaderCell>{funnelStep?.title}</BigHeaderCell>,
          disableSortBy: true,
          columns,
        };
      })
      .filter(Boolean);
  }
  return optimizedFunnelSteps.map((optimizedFunnelStep) => {
    const header: string = getHeader(optimizedFunnelStep?.funnelStep);
    const funnelStep = optimizedFunnelStep?.funnelStep;

    const isCostPerView = tableMetricsSwitch === 'cost-per';
    const isAttributed = funnelStep?.isAttributed;

    const columns =
      tableMetricsSwitch === 'cost-per'
        ? createColumnStructure({
            isAttributed,
            idPrefix: 'funnelStepCostPer',
            funnelStepId: funnelStep?.funnelStepId,
            accessorPrefix: 'funnelStepCostPer',
            isCostPerView: isCostPerView,
            funnelStepType: funnelStep?.type,
            isBaselinePredictionRescaled,
            funnelStepTitle: funnelStep?.title,
            total: {
              predicted: optimizedFunnelStep?.costPer?.predicted,
              changePercent: optimizedFunnelStep?.costPer?.changePercent,
            },
          })
        : createColumnStructure({
            isAttributed,
            idPrefix: 'funnelStepValue',
            funnelStepId: funnelStep?.funnelStepId,
            accessorPrefix: 'funnelStepValue',
            isCostPerView: isCostPerView,
            funnelStepType: funnelStep?.type,
            isBaselinePredictionRescaled,
            funnelStepTitle: funnelStep?.title,
            total: {
              attribution: optimizedFunnelStep?.attribution,
              predicted: optimizedFunnelStep?.metric?.predicted,
              changePercent: optimizedFunnelStep?.metric?.changePercent,
            },
          });

    return {
      id: `funnel-step-header-${funnelStep?.funnelStepId}`,
      title: funnelStep?.title,
      disableSticky: true,
      className: 'border-right',

      Header: (
        <BigHeaderCell style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {tableMetricsSwitch === 'cost-per' ? header : funnelStep?.title}
          {isAttributed ? ': Attributed' : null}
          {optimizedFunnelStep?.lowDataVolume ? (
            <Tooltip
              variant="dark"
              content="Low data volume"
              popperProps={{
                style: {
                  zIndex: 3305,
                },
              }}
            >
              <div>
                <SvgWarningTwo style={{ width: 24, height: 24 }} />
              </div>
            </Tooltip>
          ) : null}
        </BigHeaderCell>
      ),
      disableSortBy: true,
      columns,
    };
  });
};

const createColumnStructure = ({
  idPrefix,
  funnelStepId,
  accessorPrefix,
  isCostPerView,
  funnelStepType,
  funnelStepTitle,
  total,
  isPercent = false,
  isBaselinePredictionRescaled,
  isAttributed,
}: {
  idPrefix: 'funnelStepCostPer' | 'funnelStepValue' | 'funnelStepRoas';
  funnelStepId: number;
  accessorPrefix: string;
  isCostPerView: boolean;
  funnelStepType: NexoyaFunnelStepType;
  funnelStepTitle: string;
  total: {
    predicted?: number;
    changePercent?: number;
    attribution?: import('../../../../../types').NexoyaAttributionOptimizedMetric;
  };
  isPercent?: boolean;
  isBaselinePredictionRescaled: boolean;
  isAttributed?: boolean;
}) => {
  return [
    {
      id: `${idPrefix}_total_${funnelStepId}`,
      Header: (
        <TotalHeaderCell>
          {isNullOrUndefined(total.predicted) ? (
            '-'
          ) : isPercent ? (
            <NumberValue
              datatype={{ suffix: true, symbol: '%' }}
              variant={total.predicted > 0 ? 'positive' : 'negative'}
              value={total.predicted}
            />
          ) : idPrefix === 'funnelStepCostPer' && funnelStepType !== NexoyaFunnelStepType.ConversionValue ? (
            <FormattedCurrency withColor={true} amount={total.predicted} />
          ) : isAttributed ? (
            <Tooltip
              variant="dark"
              style={{ maxWidth: 300, wordBreak: 'break-word', padding: 0 }}
              popperProps={{
                style: {
                  zIndex: 3305,
                },
              }}
              content={getOptiAttributedTooltipContent({
                funnelStepTitle,
                attributed: total.attribution?.attributed ?? 0,
                measured: total.attribution?.measured ?? 0,
                changePercent: total?.attribution?.changePercent ?? 0,
              })}
            >
              <div>
                <NumberValue variant={total.predicted > 0 ? 'positive' : 'negative'} value={total.predicted} />
              </div>
            </Tooltip>
          ) : (
            <NumberValue variant={total.predicted > 0 ? 'positive' : 'negative'} value={total.predicted} />
          )}
        </TotalHeaderCell>
      ),
      disableSortBy: true,
      columns: [
        {
          // Stable column id across tableMetricsSwitch views

          Header: (
            <Tooltip
              style={{ maxWidth: 320, wordBreak: 'break-word' }}
              variant="dark"
              popperProps={{
                style: {
                  zIndex: 3305,
                },
              }}
              content={
                <Typography withEllipsis={false} style={{ fontSize: 12 }}>
                  This is the predicted {isCostPerView ? 'cost-per' : 'value'} for the timeframe of this optimization.
                </Typography>
              }
            >
              <HoverableTooltip>
                <div>{isCostPerView ? 'Cost-per' : 'Value'}</div>
              </HoverableTooltip>
            </Tooltip>
          ),
          accessor: `${accessorPrefix}_${funnelStepId}`,
          sortType: withSortSkipped(sortTypes.createSortByAccessor(`${accessorPrefix}_${funnelStepId}`)),
          id:
            idPrefix === 'funnelStepRoas'
              ? `funnelStep_roas_${funnelStepId}_value`
              : `funnelStep_${funnelStepId}_value`,
        },
      ],
    },
    {
      id: `${idPrefix}_total_${funnelStepId}_change`,
      className: 'border-right',
      Header: (
        <TotalHeaderCell>
          {isNullOrUndefined(total.predicted) ? (
            '-'
          ) : (
            <NumberValue
              value={total.changePercent}
              textWithColor
              showChangePrefix
              variant={total.changePercent > 0 ? 'positive' : 'negative'}
              datatype={{ suffix: true, symbol: '%' }}
              lowerIsBetter={idPrefix === 'funnelStepCostPer'}
            />
          )}
        </TotalHeaderCell>
      ),
      disableSortBy: true,
      columns: [
        {
          // Stable column id across tableMetricsSwitch views

          Header: (
            <Tooltip
              variant="dark"
              style={{ maxWidth: 280, wordBreak: 'break-word' }}
              popperProps={{
                style: {
                  zIndex: 3305,
                },
              }}
              content={
                <Typography withEllipsis={false} style={{ fontSize: 12 }}>
                  <PortfolioTypeSwitch
                    renderForBudgetType={() =>
                      isBaselinePredictionRescaled
                        ? 'This is the uplift compared to the baseline without an optimization.'
                        : 'This is the difference between the predicted values with and without the suggested budget change.'
                    }
                    renderForTargetType={() => 'This is the change compared to the baseline without an optimization.'}
                  />
                </Typography>
              }
            >
              <HoverableTooltip>
                <PortfolioTypeSwitch
                  renderForBudgetType={() => <div>{isBaselinePredictionRescaled ? '% Uplift' : '% Change'}</div>}
                  renderForTargetType={() => <div>% Change</div>}
                />
              </HoverableTooltip>
            </Tooltip>
          ),
          className: 'border-right',
          accessor: `${accessorPrefix}_${funnelStepId}_change`,
          sortType: withSortSkipped(sortTypes.createSortByAccessor(`${accessorPrefix}_${funnelStepId}_change`)),
          id:
            idPrefix === 'funnelStepRoas'
              ? `funnelStep_roas_${funnelStepId}_change`
              : `funnelStep_${funnelStepId}_change`,
        },
      ],
    },
  ];
};

export const getHeader = (funnelStep: NexoyaFunnelStepV2) => {
  const headerMap = {
    [NexoyaFunnelStepType.ConversionValue]: 'Ratio-per',
    [NexoyaFunnelStepType.Awareness]: 'CPM',
  };
  const headerStart: string = headerMap[funnelStep?.type] || 'Cost-per';
  return funnelStep?.type === NexoyaFunnelStepType.Awareness ? headerStart : `${headerStart} ${funnelStep?.title}`;
};
